import React, { useState, useContext, useEffect, useRef } from "react";
import { gql, useLazyQuery } from "@apollo/client";
import Map, { Marker, Source, Layer } from "react-map-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import { TbPlus, TbMinus } from "react-icons/tb";
import { HiddenMapGeolocateControl, MapGeolocateButton } from "./MapGeolocate";
import { BsLayers } from "react-icons/bs";
import { Title } from "../elements/Popup";
import {
  NotificationContext,
} from "@solsys/solsys-reactjs";

const gqlQuery = gql`
  query entityList($prospect_list_request: prospect_list_request,
  $crane_list_request: crane_list_request)
  {
    prospect(prospect_list_request: $prospect_list_request) {
      edges {
        id
        name
        longitude
        latitude
      }
      totalCount
    }
    crane(crane_list_request: $crane_list_request) {
      edges {
        id
        name
        longitude
        latitude
      }
      totalCount
    }
  }
`;

const ListView = ({ type, style, onSelect }) => {
  const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN || "RUNTIME_REPLACE_REACT_APP_MAPBOX_TOKEN";
  const { addNotification } = useContext(NotificationContext);
  const [renderList, setRenderList] = useState([]);
  const [viewState, setViewState] = useState({
    latitude: 0,
    longitude: 0,
    zoom: 13,
  });
  const [mapViewStyle, setMapViewStyle] = useState('mapbox://styles/mapbox/outdoors-v12');
  const [mapStyle, setMapStyle] = useState('outdoors');
  const [showStyles, setShowStyles] = useState(false);
  const [showRoads, setShowRoads] = useState(true);
  const [showLabels, setShowLabels] = useState(true);
  const [showBoundaries, setShowBoundaries] = useState(true);
  const mapRef = useRef(null);
  const geolocateRef = useRef(null);

  let variables = {
    prospect_list_request: {
      sorting: [{field: "id", direction: "ASC"}],
    },
    crane_list_request: {
      sorting: [{field: "id", direction: "ASC"}],
    },
  };

  const [refetch] = useLazyQuery(gqlQuery, {
    variables,
    pollInterval: 5000,
    onCompleted: (localData) => {
      if(type == 'prospect') setRenderList(localData?.prospect?.edges ?? []);
      else setRenderList(localData?.crane?.edges ?? []);
    },
    onError: (error) => {
      console.error(error);
      addNotification("bad", "Something went wrong");
    },
  });

  useEffect(() => {
    refetch();
  },[])

  useEffect(() => {
    if(mapStyle == 'streets') setMapViewStyle('mapbox://styles/mapbox/streets-v12');
    if(mapStyle == 'satellite') setMapViewStyle('mapbox://styles/mapbox/satellite-v9');
    if(mapStyle == 'outdoors') setMapViewStyle('mapbox://styles/mapbox/outdoors-v12');
  }, [mapStyle])

  useEffect(() => {
    const item = renderList.find(item => 
      isCoordinateValid(item.latitude, item.longitude)
    );
    if (item) {
      setViewState({latitude: item.latitude, longitude: item.longitude, zoom: 13});
      return;
    }
  },[renderList])

  const changeZoom = (value) => {
    if (mapRef.current) {
      const map = mapRef.current.getMap(); // Get the Mapbox instance
      const newZoom = map.getZoom() + value;

      map.flyTo({
        zoom: newZoom,
        duration: 1000, // Smooth animation duration in ms
        essential: true, // Ensures animation always happens
      });
    }
  }

  const isCoordinateValid = (lat, lon) => 
    lat != null && lon !=null && lat >= -90 && lat <= 90 && lon >= -180 && lon <= 180;

  return (
    <div style={style}>
      <div style={{height: 'calc(100% - 36px)'}}>
        <div label="Map" style={{height: '100%'}}>
          <Map
            ref={mapRef}
            {...viewState}
            style={{ width: "100%", height: "100%" }}
            onMove={(evt) => setViewState(evt.viewState)}
            mapStyle={mapViewStyle}
            mapboxAccessToken={MAPBOX_TOKEN}
          >
            <HiddenMapGeolocateControl ref={geolocateRef} />
            {mapStyle == 'satellite' && showRoads && <Source
              id="composite"
              type="vector"
              url="mapbox://mapbox.mapbox-streets-v8"
            >
              <Layer
                id="roads"
                type="line"
                source-layer="road"
                paint={{
                  "line-color": "#ffd700",
                  "line-width": 1,
                }}
              />
            </Source>}
    
            {mapStyle == 'satellite' && showBoundaries && <Source
              id="state-borders"
              type="vector"
              url="mapbox://mapbox.mapbox-streets-v8"
            >
              <Layer
                id="state-lines"
                type="line"
                source-layer="admin"
                filter={["all", ["==", "admin_level", 1]]} // Corrected filter syntax
                paint={{
                  "line-color": "#00ff00", // Green color for state lines
                  "line-width": 2,
                }}
              />
            </Source>}
            
            {mapStyle == 'satellite' && showLabels && <Source
              id="city-labels"
              type="vector"
              url="mapbox://mapbox.mapbox-streets-v8"
            >
              <Layer
                id="city-labels"
                type="symbol"
                source-layer="place_label"
                layout={{
                  "text-field": "{name_en}", // Label with name
                  "text-size": 12,
                }}
                paint={{
                  "text-color": "#ffffff", // White color for text
                  "text-halo-color": "#000000", // Black halo for visibility
                  "text-halo-width": 1,
                }}
              />
            </Source>}
            {renderList.map((item) => {
              return isCoordinateValid(item.latitude, item.longitude) && (
                <Marker latitude={item.latitude} longitude={item.longitude} onClick={() => onSelect(item.id)}>
                  <Title text={item.name}/>
                  <img src={`/assets/marker-${type}.png`} width={30}/>
                </Marker>
              )
            })}
          </Map>
          <div className="map-controls">
            <div className="navigation-control">
              <button className="zoom-in">
                <TbPlus className="zoom-in-icon" onClick={() => changeZoom(1)}/>
              </button>
              <button className="zoom-out">
                <TbMinus className="zoom-out-icon" onClick={() => changeZoom(-1)}/>
              </button>
              <MapGeolocateButton controlRef={geolocateRef} />
            </div>
            <div className="style-control">
              <button className="style-selector" onClick={() => setShowStyles((prev) => !prev)}>
                <BsLayers className="style-selector-icon"/>
              </button>
              {showStyles && <div className="map-styles">
                <div className="main-styles">
                  <div className={`map-style-button ${mapStyle === "streets" ? "map-style-button-active" : ""}`} onClick={() => setMapStyle('streets')}>Streets</div>
                  <div className={`map-style-button ${mapStyle === "outdoors" ? "map-style-button-active" : ""}`}  onClick={() => setMapStyle('outdoors')}>Outdoors</div>
                  <div className={`map-style-button ${mapStyle === "satellite" ? "map-style-button-active" : ""}`}  onClick={() => setMapStyle('satellite')}>Satellite</div>
                </div>
                {mapStyle == 'satellite' && <div className="source-styles">
                  <div className={`map-style-button ${showRoads ? "map-style-button-active" : ""}`} onClick={() => setShowRoads((prev) => !prev)}>Roads</div>
                  <div className={`map-style-button ${showLabels ? "map-style-button-active" : ""}`} onClick={() => setShowLabels((prev) => !prev)}>Labels</div>
                  <div className={`map-style-button ${showBoundaries ? "map-style-button-active" : ""}`} onClick={() => setShowBoundaries((prev) => !prev)}>Boundaries</div>
                </div>}
              </div>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ListView;
